import { queryOptions, useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { api } from "../http";
import { paginatedListSchema } from "@/lib/utils/zod";

export const entityFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
});
export const entitySchema = entityFormSchema.extend({
  id: z.number(),
});

export const entitiesListSchema = paginatedListSchema(entitySchema);

export type EntityFormData = z.infer<typeof entityFormSchema>;
export type Entity = z.infer<typeof entitySchema>;
export type EntitiesList = z.infer<typeof entitiesListSchema>;

type PaginationParams = {
  page: number;
  per_page?: number;
  search?: string;
};

export function entitiesListQueryOptions(params: PaginationParams) {
  return queryOptions({
    queryKey: ["entities", "list", params],
    queryFn() {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page || 10).toString(),
      });
      if (params.search) searchParams.set("search", params.search);

      return api.raw
        .get("api/v1/entities/paginated", { searchParams })
        .json<EntitiesList>();
    },
  });
}

export function allEntitiesQueryOptions(params?: {
  search?: string;
  order?: "asc" | "desc";
}) {
  return queryOptions({
    queryKey: ["entities", "list", "all", params],
    queryFn() {
      const searchParams = new URLSearchParams();

      if (params?.search) searchParams.set("search", params.search);
      if (params?.order) searchParams.set("order", params.order);

      return api.raw
        .get("api/v1/entities/", { searchParams })
        .json<Array<Entity>>();
    },
  });
}

export function entityQueryOptions(id: number) {
  return queryOptions({
    queryKey: ["entities", "detail", id],
    queryFn: async () => {
      const response = await api.raw
        .get(`api/v1/entities/${id}`)
        .json<Entity>();
      return response;
    },
  });
}

export type PolicyAnalysis = {
  final_allowed_list: string[];
  final_blocked_list: string[];
  final_redacted_list: string[];
};

export function myPolicyAnalysisQueryOptions() {
  return queryOptions({
    queryKey: ["users", "my-policy-analysis"],
    queryFn() {
      return api.raw
        .get("api/v1/users/my-policy-analysis")
        .json<PolicyAnalysis>();
    },
  });
}

// Hooks
export function useEntitiesList(params: PaginationParams) {
  return useQuery(entitiesListQueryOptions(params));
}

export function useEntity(id: number) {
  return useQuery(entityQueryOptions(id));
}
